import React from 'react';
import { UserDto, UserRoleDto } from 'models/dtoTypes';
import { date2str } from 'utils/date';
import { GetUser, UpdateUser } from 'services/user';
import { styled } from '@mui/material/styles';
import ResourceDetailContainer, { ITab } from 'kosmos/components/ResourceDetailContainer';
import { RouteComponentProps, withRouter } from 'react-router';
import { useTranslation } from 'react-i18next';

const PREFIX = 'UserDetailPage';

const classes = {
  root: `${PREFIX}-root`,
  pageHeader: `${PREFIX}-pageHeader`,
};

const Root = styled('div')(({ theme }) => ({
  position: 'relative',
  padding: theme.spacing(5),
  width: '100%',

  [`& .${classes.pageHeader}`]: {
    width: '100%',
    position: 'relative',

    '& > button': {
      position: 'absolute',
      right: 0,
      top: 0,
    },
  },
}));

function UserDetailPage(props: RouteComponentProps<{ id: string }>): JSX.Element {
  const id = props.match.params.id;
  const { t } = useTranslation('default');
  const [tabId, setTabId] = React.useState('details');

  const generateTabs = (): ITab<UserDto>[] => [
    {
      id: 'details',
      title: t('details'),
      getUiSchema: () => ({
        fields: [
          {
            label: t('id'),
            name: 'id',
            readonly: true,
            widget: 'text',
          },
          {
            label: t('email'),
            name: 'email',
            readonly: true,
            widget: 'text',
          },
          {
            label: t('first-name'),
            name: 'firstName',
            readonly: true,
            widget: 'text',
          },
          {
            label: t('role'),
            name: 'userRole',
            readonly: (user: UserDto) => user.userRole === UserRoleDto.ServiceTfp,
            widget: 'select',
            options: [
              { label: 'Admin', alias: UserRoleDto.Admin },
              { label: 'OrgAdmin', alias: UserRoleDto.OrgAdmin },
              { label: 'TFP', alias: UserRoleDto.Tfp },
              { label: 'Business', alias: UserRoleDto.Sme },
            ],
          },
          {
            label: t('last-name'),
            name: 'lastName',
            readonly: true,
            widget: 'text',
          },
          {
            label: t('organisation'),
            name: 'organisationId',
            readonly: false,
            formatter: (organisationId: string, attributes: UserDto) => attributes.organisationName,
            widget: 'organisationSelector',
          },
          {
            label: t('created-at'),
            formatter: (datestr: string) => date2str(datestr, 'DD MMM YYYY, HH:mm'),
            name: 'created',
            readonly: true,
            widget: 'text',
          },
          {
            label: t('last-updated-at'),
            name: 'updated',
            formatter: (datestr: string) => date2str(datestr, 'DD MMM YYYY, HH:mm'),
            readonly: true,
            widget: 'text',
          },
        ],
      }),
    },
  ];

  const handleFetch = async (): Promise<UserDto> => {
    return await GetUser(id);
  };

  const handleUpdate = async (attrs: { organisationId?: string }): Promise<UserDto> => {
    const user = await UpdateUser(id, attrs);
    return user;
  };

  return (
    <Root>
      <ResourceDetailContainer
        tabs={generateTabs()}
        breadcrumbText={t('users')}
        breadcrumbUrl="/users"
        onTabChanged={setTabId}
        onFetch={handleFetch}
        onUpdate={handleUpdate}
        currentTabId={tabId}
        displayNameKey="email"
      />
    </Root>
  );
}

export default withRouter(UserDetailPage);
